import dayjs from 'dayjs'

import type { TriggerSettings, DelaySettings, ActionSettings } from '../types/marketingFlow'
import type { Icons } from '@/components/base/BaseIcon.vue'
import type { AudienceTemplate } from '@/views/audience/config/templates.config'

type TemplateDetail = {
  title?: string
  desc_1: string
  desc_2?: string
  desc_3?: string
}

export type Template = {
  id: number
  type:
    | 'ConvertNewToFirstVisit'
    | 'QuickSecondVisit'
    | 'EmptyDays'
    | 'CustomerNew'
    | 'CustomerBirthday'
    | 'GoogleReviewReward'
    | 'CustomerVip'
    | 'BigSpenders'
    | 'AnniversaryCelebration'
    | 'StudentOffer'
    | 'RegularsOnPause'
    | 'UnusedPoints'
    | 'CustomerAtRisk'
    | 'PointsConsumed'
    | null
  title: string
  description: string
  isRecommended: boolean
  details: TemplateDetail[]
  italic?: string
  icon?: Icons
  tip?: string
  disabled?: boolean
  time: number
  requiresAudienceTemplate?: AudienceTemplate['type']
  audienceCreationType?: 'auto' | 'edit' | null
  category?: 'attract_convert' | 'retain_reward' | 'reactivate_reengage' | 'recommended'
}

export interface TemplateConfig extends Template {
  triggerSettings: Partial<TriggerSettings>
  delaySettings: Partial<DelaySettings>
  actionSettings: Partial<ActionSettings>
  messageContentKey?: string
  messageContentVariables?: Record<string, string>
  requiresSegmentType?: 'VIP' | 'AT_RISK'
  requiresAudienceTemplate?: AudienceTemplate['type']
  category?: 'attract_convert' | 'retain_reward' | 'reactivate_reengage'
  audienceCreationType?: 'auto' | 'edit'
}

const RECOMMENDED_TEMPLATES = ['EmptyDays', 'GoogleReviewReward', 'CustomerNew']

export const TEMPLATES_CONFIG: TemplateConfig[] = [
  {
    id: 1,
    type: 'CustomerVip',
    category: 'retain_reward',
    title: 'pages.autopilot.autopilotsPage.templates.templates.CustomerVip.title',
    icon: 'IconoirMedal1st',
    description: 'pages.autopilot.autopilotsPage.templates.templates.CustomerVip.description',
    details: [
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.CustomerVip.detail.list1.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.CustomerVip.detail.list1.desc_1',
        desc_2: 'pages.autopilot.autopilotsPage.templates.templates.CustomerVip.detail.list1.desc_2'
      },
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.CustomerVip.detail.list2.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.CustomerVip.detail.list2.desc_1',
        desc_2: 'pages.autopilot.autopilotsPage.templates.templates.CustomerVip.detail.list2.desc_2'
      }
    ],
    disabled: false,
    time: 2,
    tip: 'pages.autopilot.autopilotsPage.templates.templates.CustomerVip.tip',
    requiresSegmentType: 'VIP',
    triggerSettings: {
      triggerType: 'SEGMENT_ENTRY'
    },
    delaySettings: {
      daysBeforeAction: 15,
      sendImmediatlyAfterTrigger: false,
      sendAtSpecificHour: false,
      sendAfterDelay: true,
      delayUnit: 'MINUTES'
    },
    actionSettings: {
      actions: [
        {
          actionType: 'SEND_SMS'
        },
        {
          actionType: 'BONUS_POINTS',
          bonusPoints: 50
        }
      ]
    },
    messageContentKey: 'pages.marketingFlow.templates.CustomerVip.defaultMessage',
    messageContentVariables: { reviewLink: '{{ reviewLink }}', points: '{{ points }}' }
  },
  {
    id: 2,
    type: 'CustomerAtRisk',
    category: 'reactivate_reengage',
    title: 'pages.autopilot.autopilotsPage.templates.templates.CustomerAtRisk.title',
    icon: 'IconoirEmojiPuzzled',
    description: 'pages.autopilot.autopilotsPage.templates.templates.CustomerAtRisk.description',
    details: [
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.CustomerAtRisk.detail.list1.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.CustomerAtRisk.detail.list1.desc_1',
        desc_2: 'pages.autopilot.autopilotsPage.templates.templates.CustomerAtRisk.detail.list1.desc_2'
      },
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.CustomerAtRisk.detail.list2.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.CustomerAtRisk.detail.list2.desc_1',
        desc_2: 'pages.autopilot.autopilotsPage.templates.templates.CustomerAtRisk.detail.list2.desc_2'
      }
    ],
    disabled: false,
    time: 1,
    requiresSegmentType: 'AT_RISK',
    triggerSettings: {
      triggerType: 'SEGMENT_ENTRY'
    },
    delaySettings: {
      daysBeforeAction: 0,
      hourToSend: '11:00',
      hourToSendLabel: '11h00',
      sendImmediatlyAfterTrigger: false,
      sendAtSpecificHour: true,
      sendAfterDelay: true,
      delayUnit: 'DAYS'
    },
    actionSettings: {
      actions: [
        {
          actionType: 'SEND_SMS'
        },
        { actionType: 'REWARD' }
      ]
    },
    messageContentKey: 'pages.marketingFlow.templates.CustomerAtRisk.defaultMessage'
  },
  {
    id: 3,
    type: 'rewardRetrieval',
    category: 'retain_reward',
    title: 'pages.autopilot.autopilotsPage.templates.templates.rewardRetrieval.title',
    icon: 'IconoirTrophy',
    description: 'pages.autopilot.autopilotsPage.templates.templates.rewardRetrieval.description',
    details: [
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.rewardRetrieval.detail.list1.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.rewardRetrieval.detail.list1.desc_1'
      },
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.rewardRetrieval.detail.list2.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.rewardRetrieval.detail.list2.desc_1',
        desc_2: 'pages.autopilot.autopilotsPage.templates.templates.rewardRetrieval.detail.list2.desc_2'
      }
    ],
    disabled: true,
    time: 2,
    triggerSettings: {
      triggerType: 'REWARD_RETRIEVAL',
      selectedRewards: []
    },
    delaySettings: {
      sendImmediatlyAfterTrigger: false,
      sendAtSpecificHour: false,
      sendAfterDelay: true,
      delayUnit: 'MINUTES',
      daysBeforeAction: 30
    },
    actionSettings: {
      actions: [
        {
          actionType: 'SEND_SMS'
        }
      ]
    },
    messageContentKey: 'pages.marketingFlow.templates.rewardRetrieval.defaultMessage',
    messageContentVariables: { reviewLink: '{{ reviewLink }}' }
  },
  {
    id: 4,
    type: 'CustomerBirthday',
    category: 'retain_reward',
    title: 'pages.autopilot.autopilotsPage.templates.templates.CustomerBirthday.title',
    icon: 'IconoirBirthdayCake',
    description: 'pages.autopilot.autopilotsPage.templates.templates.CustomerBirthday.description',
    details: [
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.CustomerBirthday.detail.list1.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.CustomerBirthday.detail.list1.desc_1',
        desc_2: 'pages.autopilot.autopilotsPage.templates.templates.CustomerBirthday.detail.list1.desc_2'
      },
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.CustomerBirthday.detail.list2.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.CustomerBirthday.detail.list2.desc_1',
        desc_2: 'pages.autopilot.autopilotsPage.templates.templates.CustomerBirthday.detail.list2.desc_2',
        desc_3: 'pages.autopilot.autopilotsPage.templates.templates.CustomerBirthday.detail.list2.desc_3'
      }
    ],
    disabled: false,
    italic: 'pages.autopilot.autopilotsPage.templates.templates.CustomerBirthday.detail.italic',
    time: 3,
    triggerSettings: {
      triggerType: 'BIRTHDAY',
      dateRelativeOffsetDays: 0
    },
    delaySettings: {
      hourToSend: '11:30',
      hourToSendLabel: '11h30',
      sendImmediatlyAfterTrigger: false,
      sendAtSpecificHour: true,
      sendAfterDelay: true,
      daysBeforeAction: 0
    },
    actionSettings: {
      actions: [
        {
          actionType: 'SEND_SMS'
        },
        { actionType: 'REWARD' }
      ]
    },
    messageContentKey: 'pages.marketingFlow.templates.CustomerBirthday.defaultMessage'
  },
  {
    id: 5,
    type: 'EmptyDays',
    category: 'attract_convert',
    title: 'pages.autopilot.autopilotsPage.templates.templates.EmptyDays.title',
    icon: 'HeroCalendarIconOutline',
    description: 'pages.autopilot.autopilotsPage.templates.templates.EmptyDays.description',
    details: [
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.EmptyDays.detail.list1.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.EmptyDays.detail.list1.desc_1'
      },
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.EmptyDays.detail.list2.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.EmptyDays.detail.list2.desc_1',
        desc_2: 'pages.autopilot.autopilotsPage.templates.templates.EmptyDays.detail.list2.desc_2',
        desc_3: 'pages.autopilot.autopilotsPage.templates.templates.EmptyDays.detail.list2.desc_3'
      }
    ],
    disabled: false,
    italic: 'pages.autopilot.autopilotsPage.templates.templates.EmptyDays.detail.italic',
    time: 2,
    triggerSettings: {
      triggerType: 'CHECKIN',
      filters: [
        {
          type: 'START_DATETIME',
          value: (() => {
            const today = dayjs()
            const currentDay = today.day()
            const daysUntilNextTuesday = currentDay === 2 ? 7 : (2 - currentDay + 7) % 7

            return today.add(daysUntilNextTuesday, 'day').hour(10).minute(0).second(0).millisecond(0).toISOString()
          })()
        },
        { type: 'TIME_RANGE', value: '08:00:00.000Z - 13:00:00.000Z' },
        { type: 'WEEKDAYS', value: '2' }
      ]
    },
    delaySettings: {
      sendImmediatlyAfterTrigger: true,
      delayUnit: 'DAYS'
    },
    actionSettings: {
      actions: [
        {
          actionType: 'BONUS_MULTIPLIER',
          bonusMultiplier: 2
        }
      ]
    }
  },
  {
    id: 6,
    type: 'CustomerNew',
    category: 'retain_reward',
    title: 'pages.autopilot.autopilotsPage.templates.templates.CustomerNew.title',
    icon: 'IconoirCrackedEgg',
    description: 'pages.autopilot.autopilotsPage.templates.templates.CustomerNew.description',
    details: [
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.CustomerNew.detail.list1.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.CustomerNew.detail.list1.desc_1'
      },
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.CustomerNew.detail.list2.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.CustomerNew.detail.list2.desc_1'
      }
    ],
    disabled: false,
    time: 2,
    triggerSettings: {
      triggerType: 'CHECKIN',
      selectedAudience: null,
      selectedRewards: [],
      cooldownMinutes: null,
      multipleTimeScenario: true,
      maxTriggersPerUser: 100000,
      checkinCount: '1',
      customCheckinCount: null,
      dateRelativeOffsetDays: 0
    },
    delaySettings: {
      daysBeforeAction: '15',
      sendImmediatlyAfterTrigger: false,
      sendAfterDelay: true,
      sendAtSpecificHour: false,
      delayUnit: 'MINUTES',
      hourToSend: null,
      hourToSendLabel: null
    },
    actionSettings: {
      actions: [
        {
          actionType: 'SEND_SMS'
        }
      ]
    },
    messageContentKey: 'pages.marketingFlow.templates.customerNew.defaultMessage',
    messageContentVariables: { customerApp: '{{ customerApp }}' }
  },
  {
    id: 7,
    type: 'ConvertNewToFirstVisit',
    title: 'pages.autopilot.autopilotsPage.templates.templates.ConvertNewToFirstVisit.title',
    icon: 'IconoirGift',
    description: 'pages.autopilot.autopilotsPage.templates.templates.ConvertNewToFirstVisit.description',
    details: [
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.ConvertNewToFirstVisit.detail.list1.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.ConvertNewToFirstVisit.detail.list1.desc_1',
        desc_2: 'pages.autopilot.autopilotsPage.templates.templates.ConvertNewToFirstVisit.detail.list1.desc_2'
      },
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.ConvertNewToFirstVisit.detail.list2.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.ConvertNewToFirstVisit.detail.list2.desc_1',
        desc_2: 'pages.autopilot.autopilotsPage.templates.templates.ConvertNewToFirstVisit.detail.list2.desc_2',
        desc_3: 'pages.autopilot.autopilotsPage.templates.templates.ConvertNewToFirstVisit.detail.list2.desc_3'
      }
    ],
    disabled: false,
    time: 4,
    category: 'attract_convert',
    audienceCreationType: 'edit',
    requiresAudienceTemplate: 'convert_new_subscribers',
    triggerSettings: {
      triggerType: 'SEGMENT_ENTRY'
    },
    delaySettings: {
      daysBeforeAction: 0,
      sendImmediatlyAfterTrigger: false,
      sendAtSpecificHour: true,
      sendAfterDelay: true,
      hourToSend: '11:00',
      hourToSendLabel: '11h00',
      delayUnit: 'DAYS'
    },
    actionSettings: {
      actions: [
        {
          actionType: 'SEND_SMS'
        },
        {
          actionType: 'REWARD'
        }
      ]
    },
    messageContentKey: 'pages.marketingFlow.templates.ConvertNewToFirstVisit.defaultMessage',
    messageContentVariables: { rewardLink: '{{ rewardLink }}' }
  },
  {
    id: 8,
    type: 'QuickSecondVisit',
    title: 'pages.autopilot.autopilotsPage.templates.templates.QuickSecondVisit.title',
    icon: 'IconoirRepeat',
    description: 'pages.autopilot.autopilotsPage.templates.templates.QuickSecondVisit.description',
    details: [
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.QuickSecondVisit.detail.list1.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.QuickSecondVisit.detail.list1.desc_1',
        desc_2: 'pages.autopilot.autopilotsPage.templates.templates.QuickSecondVisit.detail.list1.desc_2'
      },
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.QuickSecondVisit.detail.list2.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.QuickSecondVisit.detail.list2.desc_1',
        desc_2: 'pages.autopilot.autopilotsPage.templates.templates.QuickSecondVisit.detail.list2.desc_2',
        desc_3: 'pages.autopilot.autopilotsPage.templates.templates.QuickSecondVisit.detail.list2.desc_3'
      }
    ],
    disabled: false,
    time: 2,
    category: 'attract_convert',
    audienceCreationType: 'edit',
    requiresAudienceTemplate: 'first_visit_recent',
    triggerSettings: {
      triggerType: 'SEGMENT_ENTRY'
    },
    delaySettings: {
      daysBeforeAction: 7,
      sendImmediatlyAfterTrigger: false,
      sendAtSpecificHour: false,
      sendAfterDelay: true,
      delayUnit: 'DAYS'
    },
    actionSettings: {
      actions: [
        {
          actionType: 'SEND_SMS'
        },
        {
          actionType: 'REWARD'
        }
      ]
    },
    messageContentKey: 'pages.marketingFlow.templates.QuickSecondVisit.defaultMessage',
    messageContentVariables: { rewardLink: '{{ rewardLink }}' }
  },
  {
    id: 9,
    type: 'GoogleReviewReward',
    category: 'retain_reward',
    title: 'pages.autopilot.autopilotsPage.templates.templates.GoogleReviewReward.title',
    icon: 'IconoirStar',
    description: 'pages.autopilot.autopilotsPage.templates.templates.GoogleReviewReward.description',
    details: [
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.GoogleReviewReward.detail.list1.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.GoogleReviewReward.detail.list1.desc_1'
      },
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.GoogleReviewReward.detail.list2.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.GoogleReviewReward.detail.list2.desc_1',
        desc_2: 'pages.autopilot.autopilotsPage.templates.templates.GoogleReviewReward.detail.list2.desc_2'
      }
    ],
    disabled: false,
    time: 2,
    triggerSettings: {
      triggerType: 'REWARD_RETRIEVAL',
      selectedRewards: []
    },
    delaySettings: {
      daysBeforeAction: 15,
      sendImmediatlyAfterTrigger: false,
      sendAtSpecificHour: false,
      sendAfterDelay: true,
      delayUnit: 'MINUTES'
    },
    actionSettings: {
      actions: [
        {
          actionType: 'SEND_SMS'
        }
      ]
    },
    messageContentKey: 'pages.marketingFlow.templates.GoogleReviewReward.defaultMessage',
    messageContentVariables: { reviewLink: '{{ reviewLink }}' }
  },
  {
    id: 10,
    type: 'BigSpenders',
    title: 'pages.autopilot.autopilotsPage.templates.templates.BigSpenders.title',
    icon: 'IconoirCoins',
    description: 'pages.autopilot.autopilotsPage.templates.templates.BigSpenders.description',
    details: [
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.BigSpenders.detail.list1.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.BigSpenders.detail.list1.desc_1',
        desc_2: 'pages.autopilot.autopilotsPage.templates.templates.BigSpenders.detail.list1.desc_2'
      },
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.BigSpenders.detail.list2.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.BigSpenders.detail.list2.desc_1',
        desc_2: 'pages.autopilot.autopilotsPage.templates.templates.BigSpenders.detail.list2.desc_2',
        desc_3: 'pages.autopilot.autopilotsPage.templates.templates.BigSpenders.detail.list2.desc_3'
      }
    ],
    disabled: false,
    time: 3,
    category: 'retain_reward',
    audienceCreationType: 'edit',
    requiresAudienceTemplate: 'BigSpenders',
    triggerSettings: {
      triggerType: 'SEGMENT_ENTRY',
      maxTriggersPerUser: null
    },
    delaySettings: {
      daysBeforeAction: 0,
      sendImmediatlyAfterTrigger: false,
      sendAtSpecificHour: true,
      sendAfterDelay: true,
      hourToSend: '11:00',
      hourToSendLabel: '11h00',
      delayUnit: 'DAYS'
    },
    actionSettings: {
      actions: [
        {
          actionType: 'SEND_SMS'
        },
        {
          actionType: 'REWARD'
        }
      ]
    },
    messageContentKey: 'pages.marketingFlow.templates.BigSpenders.defaultMessage',
    messageContentVariables: { rewardLink: '{{ rewardLink }}' }
  },
  {
    id: 11,
    type: 'AnniversaryCelebration',
    title: 'pages.autopilot.autopilotsPage.templates.templates.AnniversaryCelebration.title',
    icon: 'IconoirBirthdayCake',
    description: 'pages.autopilot.autopilotsPage.templates.templates.AnniversaryCelebration.description',
    details: [
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.AnniversaryCelebration.detail.list1.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.AnniversaryCelebration.detail.list1.desc_1',
        desc_2: 'pages.autopilot.autopilotsPage.templates.templates.AnniversaryCelebration.detail.list1.desc_2'
      },
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.AnniversaryCelebration.detail.list2.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.AnniversaryCelebration.detail.list2.desc_1',
        desc_2: 'pages.autopilot.autopilotsPage.templates.templates.AnniversaryCelebration.detail.list2.desc_2',
        desc_3: 'pages.autopilot.autopilotsPage.templates.templates.AnniversaryCelebration.detail.list2.desc_3'
      }
    ],
    disabled: false,
    time: 1,
    category: 'retain_reward',
    audienceCreationType: 'auto',
    requiresAudienceTemplate: 'one_year_loyalty',
    triggerSettings: {
      triggerType: 'SEGMENT_ENTRY'
    },
    delaySettings: {
      daysBeforeAction: 0,
      sendImmediatlyAfterTrigger: false,
      sendAtSpecificHour: true,
      sendAfterDelay: true,
      hourToSend: '11:00',
      hourToSendLabel: '11h00',
      delayUnit: 'DAYS'
    },
    actionSettings: {
      actions: [
        {
          actionType: 'SEND_SMS'
        },
        {
          actionType: 'REWARD'
        }
      ]
    },
    messageContentKey: 'pages.marketingFlow.templates.AnniversaryCelebration.defaultMessage',
    messageContentVariables: { rewardLink: '{{ rewardLink }}' }
  },
  {
    id: 12,
    type: 'StudentOffer',
    title: 'pages.autopilot.autopilotsPage.templates.templates.StudentOffer.title',
    icon: 'IconoirGraduationCap',
    description: 'pages.autopilot.autopilotsPage.templates.templates.StudentOffer.description',
    details: [
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.StudentOffer.detail.list1.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.StudentOffer.detail.list1.desc_1',
        desc_2: 'pages.autopilot.autopilotsPage.templates.templates.StudentOffer.detail.list1.desc_2'
      },
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.StudentOffer.detail.list2.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.StudentOffer.detail.list2.desc_1',
        desc_2: 'pages.autopilot.autopilotsPage.templates.templates.StudentOffer.detail.list2.desc_2',
        desc_3: 'pages.autopilot.autopilotsPage.templates.templates.StudentOffer.detail.list2.desc_3'
      }
    ],
    disabled: false,
    time: 1,
    category: 'retain_reward',
    audienceCreationType: 'auto',
    requiresAudienceTemplate: 'students',
    triggerSettings: {
      triggerType: 'SEGMENT_ENTRY'
    },
    delaySettings: {
      daysBeforeAction: 15,
      sendImmediatlyAfterTrigger: false,
      sendAtSpecificHour: false,
      sendAfterDelay: true,
      delayUnit: 'MINUTES'
    },
    actionSettings: {
      actions: [
        {
          actionType: 'SEND_SMS'
        },
        {
          actionType: 'BONUS_POINTS',
          bonusPoints: 50
        }
      ]
    },
    messageContentKey: 'pages.marketingFlow.templates.StudentOffer.defaultMessage',
    messageContentVariables: { points: '{{ points }}' }
  },

  {
    id: 13,
    type: 'RegularsOnPause',
    title: 'pages.autopilot.autopilotsPage.templates.templates.RegularsOnPause.title',
    icon: 'IconoirBell',
    description: 'pages.autopilot.autopilotsPage.templates.templates.RegularsOnPause.description',
    details: [
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.RegularsOnPause.detail.list1.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.RegularsOnPause.detail.list1.desc_1'
      },
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.RegularsOnPause.detail.list2.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.RegularsOnPause.detail.list2.desc_1',
        desc_2: 'pages.autopilot.autopilotsPage.templates.templates.RegularsOnPause.detail.list2.desc_2',
        desc_3: 'pages.autopilot.autopilotsPage.templates.templates.RegularsOnPause.detail.list2.desc_3'
      }
    ],
    disabled: false,
    time: 3,
    category: 'reactivate_reengage',
    audienceCreationType: 'edit',
    requiresAudienceTemplate: 'RegularsOnPause',
    triggerSettings: {
      triggerType: 'SEGMENT_ENTRY',
      maxTriggersPerUser: null
    },
    delaySettings: {
      daysBeforeAction: 0,
      sendImmediatlyAfterTrigger: false,
      sendAtSpecificHour: true,
      sendAfterDelay: true,
      hourToSend: '11:00',
      hourToSendLabel: '11h00',
      delayUnit: 'DAYS'
    },
    actionSettings: {
      actions: [
        {
          actionType: 'SEND_SMS'
        },
        {
          actionType: 'REWARD'
        }
      ]
    },
    messageContentKey: 'pages.marketingFlow.templates.RegularsOnPause.defaultMessage',
    messageContentVariables: { rewardLink: '{{ rewardLink }}' }
  },
  {
    id: 14,
    type: 'UnusedPoints',
    title: 'pages.autopilot.autopilotsPage.templates.templates.UnusedPoints.title',
    icon: 'IconoirHalfMoon',
    description: 'pages.autopilot.autopilotsPage.templates.templates.UnusedPoints.description',
    details: [
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.UnusedPoints.detail.list1.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.UnusedPoints.detail.list1.desc_1'
      },
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.UnusedPoints.detail.list2.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.UnusedPoints.detail.list2.desc_1',
        desc_2: 'pages.autopilot.autopilotsPage.templates.templates.UnusedPoints.detail.list2.desc_2'
      }
    ],
    disabled: false,
    time: 2,
    category: 'reactivate_reengage',
    audienceCreationType: 'auto',
    requiresAudienceTemplate: 'UnusedPoints',
    triggerSettings: {
      triggerType: 'SEGMENT_ENTRY'
    },
    delaySettings: {
      daysBeforeAction: 0,
      sendImmediatlyAfterTrigger: false,
      sendAtSpecificHour: true,
      sendAfterDelay: true,
      hourToSend: '11:00',
      hourToSendLabel: '11h00',
      delayUnit: 'DAYS'
    },
    actionSettings: {
      actions: [
        {
          actionType: 'SEND_SMS'
        }
      ]
    },
    messageContentKey: 'pages.marketingFlow.templates.UnusedPoints.defaultMessage'
  },
  {
    id: 15,
    type: 'PointsConsumed',
    title: 'pages.autopilot.autopilotsPage.templates.templates.PointsConsumed.title',
    icon: 'IconoirRefreshCircle',
    description: 'pages.autopilot.autopilotsPage.templates.templates.PointsConsumed.description',
    details: [
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.PointsConsumed.detail.list1.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.PointsConsumed.detail.list1.desc_1',
        desc_2: 'pages.autopilot.autopilotsPage.templates.templates.PointsConsumed.detail.list1.desc_2'
      },
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.PointsConsumed.detail.list2.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.PointsConsumed.detail.list2.desc_1',
        desc_2: 'pages.autopilot.autopilotsPage.templates.templates.PointsConsumed.detail.list2.desc_2',
        desc_3: 'pages.autopilot.autopilotsPage.templates.templates.PointsConsumed.detail.list2.desc_3'
      }
    ],
    disabled: false,
    time: 3,
    category: 'reactivate_reengage',
    audienceCreationType: 'auto',
    requiresAudienceTemplate: 'PointsConsumed',
    triggerSettings: {
      triggerType: 'SEGMENT_ENTRY'
    },
    delaySettings: {
      daysBeforeAction: 15,
      sendImmediatlyAfterTrigger: false,
      sendAtSpecificHour: false,
      sendAfterDelay: true,
      delayUnit: 'MINUTES'
    },
    actionSettings: {
      actions: [
        {
          actionType: 'SEND_SMS'
        },
        {
          actionType: 'BONUS_POINTS',
          bonusPoints: 10
        }
      ]
    },
    messageContentKey: 'pages.marketingFlow.templates.PointsConsumed.defaultMessage'
  },
  {
    id: 16,
    type: 'RewardNearToUnlock',
    title: 'pages.autopilot.autopilotsPage.templates.templates.RewardNearToUnlock.title',
    icon: 'IconoirTrophy',
    description: 'pages.autopilot.autopilotsPage.templates.templates.RewardNearToUnlock.description',
    details: [
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.RewardNearToUnlock.detail.list1.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.RewardNearToUnlock.detail.list1.desc_1'
      },
      {
        title: 'pages.autopilot.autopilotsPage.templates.templates.RewardNearToUnlock.detail.list2.title',
        desc_1: 'pages.autopilot.autopilotsPage.templates.templates.RewardNearToUnlock.detail.list2.desc_1',
        desc_2: 'pages.autopilot.autopilotsPage.templates.templates.RewardNearToUnlock.detail.list2.desc_2'
      }
    ],
    disabled: false,
    time: 2,
    category: 'retain_reward',
    triggerSettings: {
      triggerType: 'REWARD_NEAR_TO_UNLOCK',
      selectedAudience: null,
      selectedRewards: [],
      cooldownMinutes: 21600,
      maxTriggersPerUser: 3,
      checkinCount: null,
      customCheckinCount: null,
      dateRelativeOffsetDays: 0,
      pointTreshold: 10,
      multipleTimeScenario: true
    },
    delaySettings: {
      daysBeforeAction: '0',
      sendImmediatlyAfterTrigger: false,
      sendAtSpecificHour: true,
      sendAfterDelay: true,
      delayUnit: 'DAYS',
      hourToSend: '11:00',
      hourToSendLabel: '11h00'
    },
    actionSettings: {
      actions: [
        {
          actionType: 'SEND_SMS',
          messageContent: 'pages.marketingFlow.templates.RewardNearToUnlock.defaultMessage',
          bonusPoints: null,
          bonusMultiplier: null,
          preferPushChannel: false,
          preferSmsChannel: false,
          hasQrCodeLink: false
        }
      ],
      conversionDelay: 7,
      isTransactional: false
    },
    messageContentKey: 'pages.marketingFlow.templates.RewardNearToUnlock.defaultMessage'
  }
].map((template) => ({
  ...template,
  isRecommended: RECOMMENDED_TEMPLATES.includes(template.type)
})) as TemplateConfig[]
